<script lang="ts">
  import {onMount} from 'svelte';
  import {set, get} from 'idb-keyval';
  import {
    validateSubmission,
    getUserId,
    groupBy,
    postResult,
    DIV,
    getCurrentQuestionList,
    onSurveyLoad,
    clearCache
  } from './utils';
  import type {Page, CurrentResponses, Survey, LogEvent} from './types';
  import QuestionDispatch from './questions/QuestionDispatch.svelte';
  export let survey: Survey;

  let loading: boolean = true;

  let {pages, name, startPage} = survey;
  $: pageIndex = groupBy<Page>(pages, 'pageName');

  let userId: string;
  let responses: CurrentResponses = {};
  let events: LogEvent[] = [{time: Date.now(), questionId: `special${DIV}initialize`, value: ''}];
  let validations: Record<string, string> = {};
  function onChange({detail: {questionId, newValue, noEventLog}}: any) {
    responses[questionId] = newValue;
    events.push({time: Date.now(), questionId, value: `${newValue}`});
    if (!noEventLog) {
      set(`${survey.urlName}-events`, events);
    }
    set(`${survey.urlName}-responses`, responses);
  }

  $: surveyComplete = false;
  $: currentPageName = startPage;
  $: currentPage = pageIndex[currentPageName];
  let pageStack: string[] = [startPage];
  $: questionList = responses && getCurrentQuestionList(currentPage, responses);

  $: subText = currentPage.couldBeFinal ? 'Submit' : 'Next';

  function updatePageCache() {
    set(`${survey.urlName}-pageStack`, pageStack);
    set(`${survey.urlName}-currentPageName`, currentPageName);
  }

  let thereHasBeenAnError = false;
  onMount(async () => {
    try {
      userId = await getUserId();
      events = (await get(`${survey.urlName}-events`)) || [];
      responses = (await get(`${survey.urlName}-responses`)) || {};
      pageStack = (await get(`${survey.urlName}-pageStack`)) || [];
      pageStack.forEach((page, idx) => {
        const nextPage = pageIndex[page].nextPage(responses, pageStack.slice(0, idx));
        if (typeof nextPage !== 'string') {
          pageIndex[nextPage.pageName] = nextPage;
        }
      });
      currentPageName = (await get(`${survey.urlName}-currentPageName`)) || survey.startPage;
      loading = false;
    } catch (e) {
      console.log(e);
      thereHasBeenAnError = true;
    }
    onSurveyLoad(survey.urlName);
  });

  // todo on page load focus on first page element?

  let state: 'loading' | 'ready' | 'complete' | 'survey-ended' | 'crashed' = 'loading';
  $: evalState({loading, surveyComplete, thereHasBeenAnError});
  function evalState({loading, surveyComplete, thereHasBeenAnError}: Record<string, boolean>) {
    if (thereHasBeenAnError) {
      state = 'crashed';
      return;
    }
    if (survey.endDate < new Date()) {
      state = 'survey-ended';
      return;
    }
    if (loading) {
      state = 'loading';
      return;
    }
    state = surveyComplete ? 'complete' : 'ready';
  }
</script>

<svelte:head>
  <title>{name}</title>
  <html lang="en" />
</svelte:head>

<main class="dark:bg-zinc-900 h-fit min-h-full">
  <div class="bg-black text-white text-2xl text-center p-2 dark:bg-zinc-700">{name}</div>
  <div class="py-8 px-4 flex justify-center items-center">
    <div class="md:w-1/2 w-full">
      {#if state === 'crashed'}
        <div class="flex flex-col justify-center items-center">
          <h1 class="text-3xl">There has been an error</h1>
          <button
            on:click={() => {
              clearCache(survey.urlName).then(() => location.reload());
            }}
            class="border-2 cursor-pointer">Reset Survey</button
          >
        </div>
      {:else if state === 'ready'}
        <form
          on:submit|preventDefault={(e) => {
            const valResult = validateSubmission(currentPage, responses);
            validations = valResult.validations;
            if (!valResult.allValid) {
              return;
            }
            // get the next page and save it to the page stack
            const nextPage = currentPage.nextPage(responses, pageStack);
            const isPage = typeof nextPage !== 'string';
            pageStack = [...pageStack, isPage ? nextPage.pageName : nextPage];

            // figure out how to handle the next page
            if (isPage) {
              pageIndex[nextPage.pageName] = nextPage;
              currentPageName = nextPage.pageName;
              updatePageCache();
            } else if (nextPage !== 'finish') {
              currentPageName = nextPage;
              updatePageCache();
            } else {
              surveyComplete = true;
              events.push({time: Date.now(), questionId: `special${DIV}survey-completed`, value: ''});
              postResult(responses, userId, survey, events, pageStack).then(() => clearCache(survey.urlName));
            }
            // finally scroll to the top
            document.body.scrollIntoView();
          }}
        >
          {#each questionList as question}
            <QuestionDispatch
              bind:question
              pageQuestionIdx={`${currentPageName}${DIV}${question.id}`}
              on:change={onChange}
              validations={validations[`${currentPageName}${DIV}${question.id}`]}
              currentResponses={responses}
            />
          {/each}
          <div class="flex w-full items-center justify-center mt-16">
            {#if currentPage.allowBack && pageStack.length > 1}
              <input
                type="submit"
                on:click|preventDefault={() => {
                  pageStack.pop();
                  const prevPage = pageStack[pageStack.length - 1];
                  if (!prevPage) {
                    return;
                  }
                  pageStack = [...pageStack];
                  currentPageName = prevPage;
                  updatePageCache();
                }}
                value="Back"
                class="w-full bg-black text-white border-r-2 border-white cursor-pointer dark:bg-zinc-700"
              />
            {/if}
            <input
              type="submit"
              value={subText}
              class="w-full bg-black text-white cursor-pointer dark:bg-zinc-700"
            />
          </div>
        </form>
      {:else if state === 'complete'}
        <div class="w-full h-full flex items-center justify-center dark:text-white">
          <h1 class="text-3xl">Thanks for participating in our survey</h1>
        </div>
      {:else if state === 'survey-ended'}
        <div class="w-full h-full flex items-center justify-center dark:text-white">
          <h1 class="text-3xl">This is survey is no longer accepting submissions</h1>
        </div>
      {/if}
    </div>
  </div>
</main>
